import { supabase } from '@/integrations/supabase/client';
import { isOrderLockedForCart } from '@/utils/orderLock';

/**
 * Pedido paralelo na origem: o ponteiro do pedido (`ameup_parent_event_id`) mora no sessionStorage, que some quando o cliente
 * volta de outro aplicativo (banco, Pix) e o navegador do celular abre uma aba nova. A loja, sem ponteiro, criava um pedido novo
 * com os mesmos itens (MAR-3CE8, gêmeo da proposta MAR-5B23). Antes de criar, a loja procura um pedido ABERTO da mesma conta,
 * do mesmo fornecedor e da mesma data e continua nele.
 */
export interface OpenOrderCandidate {
  id: string;
  event_code?: string | null;
  status?: string | null;
  status_payments?: string | null;
  is_manual?: boolean | null;
  is_active?: boolean | null;
  parent_event_id?: string | null;
  updated_at?: string | null;
}

const normalize = (value: unknown): string =>
  String(value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Pedido que ainda pode receber o carrinho: ativo, de cliente, não vendido, não excluído nem cancelado. */
export const isReusableOpenOrder = (order: OpenOrderCandidate): boolean => {
  if (order.is_active === false || order.is_manual === true || order.parent_event_id) return false;
  if (isOrderLockedForCart(order)) return false;
  const status = normalize(order.status);
  return !status.startsWith('exclu') && !status.startsWith('cancel');
};

/** Entre os candidatos, o aberto atualizado por último (ou nenhum). */
export const pickReusableOrder = (orders: readonly OpenOrderCandidate[]): OpenOrderCandidate | null => {
  const open = orders.filter(isReusableOpenOrder);
  if (open.length === 0) return null;
  return [...open].sort((a, b) => String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? '')))[0];
};

/** Procura no banco o pedido aberto da conta para o fornecedor e a data. Falha de leitura = nenhum (nunca bloqueia a criação). */
export async function findOpenOrderToReuse(params: { userId: string; supplierId: string; eventDate: string }): Promise<OpenOrderCandidate | null> {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('id, event_code, status, status_payments, is_manual, is_active, parent_event_id, updated_at')
      .eq('user_id', params.userId)
      .eq('event_date', params.eventDate)
      .is('parent_event_id', null)
      .or(`supplier_id.eq.${params.supplierId},created_by_supplier_id.eq.${params.supplierId}`)
      .order('updated_at', { ascending: false })
      .limit(10);
    if (error) return null;
    return pickReusableOrder((data ?? []) as OpenOrderCandidate[]);
  } catch {
    return null;
  }
}
